"use client";

import { memo, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedditAlien, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { Newspaper } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { SourceDisplay } from "./SourceDisplay";

interface UnifiedSearchData {
  analysis: string;
  sources: Array<{
    text: string;
    author: string;
    score: number | null;
    url: string;
    post_title?: string;
    created_at: string;
    source_type: string;
    originalIndex?: number;
  }>;
  query?: string;
  title?: string;
  search_type?: string;
  display_mode?: string;
  metadata?: any;
  date_range?: any;
}

interface UnifiedSearchCarouselProps {
  results: UnifiedSearchData[];
  onCitationClick?: (citation: number) => void;
}

const SOURCE_TYPE_ORDER = ["news", "twitter", "reddit"];

const normalizeSourceType = (sourceType: string) =>
  String(sourceType || "").trim().toLowerCase();

const getSourceTypeLabel = (sourceType: string) => {
  if (sourceType === "twitter") return "Twitter";
  if (sourceType === "reddit") return "Reddit";
  if (sourceType === "news") return "News";
  return sourceType || "Search";
};

function UnifiedSearchCarouselComponent({ results }: UnifiedSearchCarouselProps) {
  const [activeSourceType, setActiveSourceType] = useState<string>("");

  const groupedResults = useMemo(() => {
    const groups = new Map<
      string,
      {
        sourceType: string;
        label: string;
        title?: string;
        sources: UnifiedSearchData["sources"];
      }
    >();

    results.forEach((result) => {
      const sourceType =
        normalizeSourceType(
          result.search_type ||
            result.sources?.[0]?.source_type ||
            result.title ||
            result.query ||
            ""
        ) || "search";

      if (!groups.has(sourceType)) {
        groups.set(sourceType, {
          sourceType,
          label: getSourceTypeLabel(sourceType),
          title: result.title || result.query,
          sources: [],
        });
      }

      const group = groups.get(sourceType)!;
      const startingIndex = group.sources.length;

      (result.sources || []).forEach((source, sourceIndex) => {
        group.sources.push({
          ...source,
          source_type: normalizeSourceType(source.source_type) || sourceType,
          originalIndex: startingIndex + sourceIndex + 1,
        });
      });

      if (!group.title && (result.title || result.query)) {
        group.title = result.title || result.query;
      }
    });

    return Array.from(groups.values()).sort((a, b) => {
      const aIndex = SOURCE_TYPE_ORDER.indexOf(a.sourceType);
      const bIndex = SOURCE_TYPE_ORDER.indexOf(b.sourceType);
      const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (normalizedA !== normalizedB) return normalizedA - normalizedB;
      return a.label.localeCompare(b.label);
    });
  }, [results]);

  const groupedResultsKey = groupedResults
    .map((group) => `${group.sourceType}:${group.sources.length}`)
    .join("|");

  useEffect(() => {
    setActiveSourceType((prev) => {
      if (prev && groupedResults.some((group) => group.sourceType === prev)) {
        return prev;
      }
      return groupedResults[0]?.sourceType || "";
    });
  }, [groupedResults, groupedResultsKey]);

  if (!results || results.length === 0 || groupedResults.length === 0) return null;

  const activeGroup =
    groupedResults.find((group) => group.sourceType === activeSourceType) ||
    groupedResults[0];

  const renderSourceTypeIcon = (sourceType: string, className?: string) => {
    if (sourceType === "twitter") {
      return <FontAwesomeIcon icon={faXTwitter} className={className} />;
    }
    if (sourceType === "reddit") {
      return <FontAwesomeIcon icon={faRedditAlien} className={className} />;
    }
    if (sourceType === "news") {
      return <Newspaper className={className} strokeWidth={2} />;
    }
    return null;
  };

  return (
    <div className="w-full space-y-4">
      {groupedResults.length > 1 && activeGroup && (
        <Tabs
          value={activeGroup.sourceType}
          onValueChange={setActiveSourceType}
          className="w-full"
        >
          <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-muted/30 p-1">
            {groupedResults.map((group) => (
              <TabsTrigger
                key={group.sourceType}
                value={group.sourceType}
                // Stable selector for the buddy tour click_target beat
                // on the X (twitter) tab. Avoids depending on label
                // text or DOM order of the tabs.
                data-source-type={group.sourceType}
                className="h-9 gap-2 rounded-lg px-3 text-xs font-medium"
              >
                {renderSourceTypeIcon(
                  group.sourceType,
                  cn(
                    "h-3.5 w-3.5",
                    group.sourceType === "twitter" && "text-foreground",
                    group.sourceType === "reddit" && "text-orange-500",
                    group.sourceType === "news" && "text-emerald-400"
                  )
                )}
                <span>{group.label}</span>
                <span className="rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
                  {group.sources.length}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {activeGroup?.title && groupedResults.length > 1 && (
        <div className="px-1 text-xs font-medium text-muted-foreground">
          {activeGroup.title}
        </div>
      )}

      {activeGroup?.sources.length ? (
        <SourceDisplay sources={activeGroup.sources} />
      ) : (
        <div className="rounded-xl border border-border/80 bg-background/30 p-6 text-sm text-muted-foreground">
          No {activeGroup?.label?.toLowerCase() || "search"} results found.
        </div>
      )}
    </div>
  );
}

export const UnifiedSearchCarousel = memo(UnifiedSearchCarouselComponent);
